import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format, isSameDay, parseISO } from "date-fns";
import { CalendarIcon, Clock, User } from "lucide-react";
import { type Stream, type Speaker } from "@shared/schema";

export default function CalendarPage() {
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());
  
  const { data: streams = [] } = useQuery<Stream[]>({
    queryKey: ["/api/streams"],
  });

  const { data: speakers = [] } = useQuery<Speaker[]>({
    queryKey: ["/api/speakers"],
  });

  // Filter streams for the selected date
  const streamsForSelectedDate = streams.filter((stream: any) => {
    if (!selectedDate) return false;
    const streamDate = parseISO(stream.date);
    return isSameDay(streamDate, selectedDate);
  });

  // Get dates that have streams for calendar highlighting
  const datesWithStreams = streams.map((stream: any) => parseISO(stream.date));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Stream Calendar</h1>
        <p className="text-muted-foreground">
          View and manage your scheduled live streams
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Schedule</CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
              className="rounded-md border"
              modifiers={{
                hasStream: datesWithStreams,
              }}
              modifiersStyles={{
                hasStream: {
                  fontWeight: "bold",
                  textDecoration: "underline",
                },
              }}
              data-testid="calendar-view"
            />
          </CardContent>
        </Card>

        {/* Streams for selected date */}
        <Card>
          <CardHeader>
            <CardTitle>
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                {selectedDate ? format(selectedDate, "MMM d, yyyy") : "Select a date"}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {streamsForSelectedDate.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8" data-testid="no-streams-message">
                No streams scheduled for this date
              </p>
            ) : (
              <div className="space-y-4">
                {streamsForSelectedDate.map((stream: any) => {
                  const speaker = speakers.find((s: any) => s.id === stream.speakerId);
                  const speakerName = speaker 
                    ? `${speaker.firstName} ${speaker.lastName}`
                    : "Unknown Speaker";

                  return (
                    <div
                      key={stream.id}
                      className="p-4 border rounded-lg space-y-2 hover:bg-muted/30 transition-colors"
                      data-testid={`stream-card-${stream.id}`}
                    >
                      <h3 className="font-semibold">{stream.title}</h3>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{stream.time}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <User className="w-4 h-4" />
                        <span>{speakerName}</span>
                      </div>
                      
                      <div className="flex gap-1 flex-wrap">
                        {stream.platforms?.map((platform: any, idx: number) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {platform.name}
                          </Badge>
                        ))}
                      </div>
                      
                      <Badge 
                        className="text-xs"
                        variant={stream.status === "scheduled" ? "default" : "secondary"}
                      >
                        {stream.status}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* All Upcoming Streams */}
      <Card>
        <CardHeader>
          <CardTitle>All Upcoming Streams</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {streams
              .filter((stream: any) => {
                const streamDate = parseISO(stream.date);
                return streamDate >= new Date() || isSameDay(streamDate, new Date());
              })
              .map((stream: any) => {
                const speaker = speakers.find((s: any) => s.id === stream.speakerId);
                const speakerName = speaker
                  ? `${speaker.firstName} ${speaker.lastName}`
                  : "Unknown Speaker";
                const streamDate = parseISO(stream.date);

                return (
                  <div
                    key={stream.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/30 transition-colors"
                    data-testid={`upcoming-stream-${stream.id}`}
                  >
                    <div className="flex-1">
                      <h4 className="font-medium">{stream.title}</h4>
                      <p className="text-sm text-muted-foreground">
                        {format(streamDate, "EEE, MMM d")} at {stream.time} • {speakerName}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {stream.platforms?.map((platform: any, idx: number) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {platform.name.toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
